import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import { PointOfSale, Inventory, People, TrendingUp, Warning, AccountBalance } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { SalesService } from '../services/salesService';
import { InventoryService } from '../services/inventoryService';
import { ExpenseService } from '../services/expenseService';
import { Product, Sale } from '../types';

interface DashboardStats {
  todaySales: number;
  lowStockCount: number;
  totalCustomers: number;
  monthlyProfit: number;
  monthlyExpenses: number;
}

export default function Dashboard() {
  const { appUser } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    todaySales: 0,
    lowStockCount: 0,
    totalCustomers: 0,
    monthlyProfit: 0,
    monthlyExpenses: 0,
  });
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);
  const [recentSales, setRecentSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!appUser?.salonId) return;

      try {
        setLoading(true);

        // Load all dashboard data in parallel
        const [
          todaySales,
          lowStockProductsData,
          recentSalesData
        ] = await Promise.all([
          SalesService.getTodaySalesTotal(appUser.salonId),
          InventoryService.getLowStockProducts(appUser.salonId),
          SalesService.getSales(appUser.salonId).then(sales => sales.slice(0, 5)),
        ]);

        // Calculate monthly profit (actual sales minus actual expenses)
        const currentMonth = new Date();
        currentMonth.setDate(1);
        const nextMonth = new Date(currentMonth);
        nextMonth.setMonth(nextMonth.getMonth() + 1);

        const monthlySales = await SalesService.getSales(appUser.salonId, currentMonth, nextMonth);
        const monthlyExpenses = await ExpenseService.getExpensesInRange(appUser.salonId, currentMonth, nextMonth);

        const monthlySalesTotal = monthlySales.reduce((sum, sale) => sum + sale.totalAmount, 0);
        const monthlyExpensesTotal = monthlyExpenses.reduce((sum, expense) => sum + expense.amount, 0);
        const monthlyProfit = monthlySalesTotal - monthlyExpensesTotal;

        setStats({
          todaySales,
          lowStockCount: lowStockProductsData.length,
          totalCustomers: 0, // TODO: Implement customer counting
          monthlyProfit,
          monthlyExpenses: monthlyExpensesTotal,
        });

        setLowStockProducts(lowStockProductsData);
        setRecentSales(recentSalesData);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [appUser]);

  const statCards = [
    {
      title: 'Today\'s Sales',
      value: `₦${stats.todaySales.toLocaleString()}`,
      icon: <PointOfSale color="primary" />,
    },
    {
      title: 'Monthly Expenses',
      value: `₦${stats.monthlyExpenses.toLocaleString()}`,
      icon: <AccountBalance color="error" />,
    },
    {
      title: 'Low Stock Items',
      value: stats.lowStockCount.toString(),
      icon: <Inventory color="secondary" />,
      warning: stats.lowStockCount > 0,
    },
    {
      title: 'Monthly Profit',
      value: `₦${stats.monthlyProfit.toLocaleString()}`,
      icon: <TrendingUp color={stats.monthlyProfit >= 0 ? "success" : "error"} />,
      warning: stats.monthlyProfit < 0,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Welcome back! Here's your business overview.
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card sx={stat.warning ? { border: '1px solid #ff9800' } : {}}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  {stat.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {stat.title}
                  </Typography>
                  {stat.warning && (
                    <Warning color="warning" sx={{ ml: 'auto' }} />
                  )}
                </Box>
                <Typography variant="h4" color={stat.warning ? "warning.main" : "primary"}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Box mt={3}>
          <Alert severity="warning">
            <Typography variant="h6">Low Stock Alert</Typography>
            <Box mt={1}>
              {lowStockProducts.slice(0, 3).map((product) => (
                <Chip
                  key={product.id}
                  label={`${product.name}: ${product.currentStock} left`}
                  color="warning"
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
              {lowStockProducts.length > 3 && (
                <Chip
                  label={`+${lowStockProducts.length - 3} more`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Alert>
        </Box>
      )}

      {/* Recent Sales */}
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          Recent Sales
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading recent activity...
          </Typography>
        ) : recentSales.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sales recorded yet. Start by recording your first sale!
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {recentSales.map((sale) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sale.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">
                        ₦{sale.totalAmount.toLocaleString()}
                      </Typography>
                      <Chip
                        label={sale.paymentMethod}
                        size="small"
                        color={sale.paymentMethod === 'cash' ? 'success' : 'primary'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {sale.items.length} item(s)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {sale.createdAt.toLocaleDateString()} at {sale.createdAt.toLocaleTimeString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Quick Actions */}
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          Quick Actions
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Common tasks to get you started
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Chip
            label="Record New Sale"
            color="primary"
            clickable
            onClick={() => window.location.href = '/sales'}
          />
          <Chip
            label="Add Inventory"
            color="secondary"
            clickable
            onClick={() => window.location.href = '/inventory'}
          />
          <Chip
            label="View Reports"
            variant="outlined"
            clickable
            onClick={() => window.location.href = '/reports'}
          />
        </Box>
      </Box>
    </Box>
  );
}
